import {
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  useBusinessModels,
  useCompanySteps,
  useHandlePageTraversal,
} from "../../../../common/hooks";
import { HookedCheckbox } from "../../../components/HookedCheckbox";
import { HookedForm } from "../../../components/HookedForm";
import { CompaniesStep, RoutePaths } from "../../../consts/enums";
import { HookedNumberField } from "../../../components/HookedNumberField";
import { EventName, trackEvent } from "../../../../common/utils/helpers";

export const SellerFinancials = () => {
  const { id: startupId } = useParams();
  if (startupId === undefined) {
    return <Navigate to="/dashboard" replace={true} />;
  }
  const { data, isLoading, isError } = useCompanySteps(
    CompaniesStep.STARTUP,
    startupId
  );
  const navigate = useNavigate();
  const formHook = useForm();
  const { handleNext } = useHandlePageTraversal(formHook, Number(startupId));
  const { data: businessModels, isLoading: loadingBusiness } =
    useBusinessModels(startupId);

  useEffect(() => {
    if (data && !isLoading) {
      formHook.reset({ ...data });
    }
  }, [data, isLoading]);

  if (isError) {
    return <Navigate to="/dashboard" replace={true} />;
  }
  return (
    <Box
      sx={{
        backgroundColor: "common.black",
        backgroundImage: "url(/images/BiggerGradient.png)",
        backgroundSize: "cover",
        backgroundPosition: "right",
        backgroundRepeat: "no-repeat",
        width: "100%",
        minHeight: "100%",
      }}
    >
      <Container maxWidth="md" sx={{ padding: 4 }}>
        <Typography variant="h3" sx={{ color: "white" }}>
          Complete your company profile - this process takes 3 minutes
        </Typography>
        <Typography variant="subtitle1" sx={{ color: "common.grayOpacity" }}>
          List your company profile anonymously and start receiving messages
          from interested buyers
        </Typography>
        <Paper
          variant="outlined"
          elevation={2}
          sx={{ marginTop: 4, padding: 3 }}
        >
          <Typography variant="h3" sx={{ marginBottom: 1 }}>
            How do you generate revenue?
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "common.gray", marginBottom: 2 }}
          >
            Please select revenue streams.
          </Typography>
          <HookedForm formHook={formHook}>
            <HookedCheckbox
              name="business_models"
              loading={loadingBusiness}
              options={businessModels?.map((x) => ({
                value: x.id,
                label: x.name,
              }))}
            />
            <Box
              component="hr"
              sx={{
                width: "100%",
                borderColor: "common.lightgray",
                borderStyle: "solid",
                marginY: 2,
              }}
            />
            <Typography variant="h3" sx={{ marginBottom: 1 }}>
              What is your EBITDA and revenue for the most recent financial
              year?
            </Typography>
            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              marginBottom={2}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ width: "100%" }}
              >
                EBITDA
              </Typography>
              <HookedNumberField
                hiddenLabel
                currency
                variant="outlined"
                name="annual_profit"
              />
            </Stack>
            <Stack direction="row" spacing={3} alignItems="center">
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ width: "100%" }}
              >
                Revenue
              </Typography>
              <HookedNumberField
                hiddenLabel
                currency
                variant="outlined"
                name="annual_revenue"
              />
            </Stack>
          </HookedForm>
        </Paper>
        <Box
          sx={{
            textAlign: "right",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() =>
              handleNext(RoutePaths.REGISTER_SELLER + `/${startupId}/category`)
            }
            sx={{ marginTop: 2 }}
            variant="roundsecondary"
          >
            Back
          </Button>
          <Button
            onClick={() =>
              handleNext(RoutePaths.REGISTER_SELLER + `/${startupId}/location`)
            }
            sx={{ marginTop: 2 }}
            variant="roundgradient"
          >
            Save &amp; Continue
          </Button>
        </Box>

        <Box
          sx={{
            display: { xs: "flex", md: "block" },
            flexDirection: "column",
            marginTop: 8,
          }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
            color="white"
            sx={{ marginBottom: 1 }}
          >
            Just want to enter the marketplace?
          </Typography>
          <Typography
            variant="body2"
            color="common.gray"
            sx={{ marginBottom: 2 }}
          >
            Come back later to complete your Seller Profile.
          </Typography>
          <Button
            variant="roundprimary"
            onClick={() => {
              trackEvent(EventName.SKIP_SIGN_UP);
              navigate(RoutePaths.YOU_ACQUISITION);
            }}
          >
            <span>Skip</span>
          </Button>
        </Box>
      </Container>
    </Box>
  );
};
